import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getUserProfile, updateUserProfile, setupProgramByPersona } from '../utils/database';
import ProgramSelector from '../components/ProgramSelector';
import BuildingPlanScreen from '../components/BuildingPlanScreen';
import DashboardSidebar from '../components/DashboardSidebar';
import BottomNav from '../components/BottomNav';
import '../styles/Dashboard.css';
import '../styles/Onboarding.css';

/**
 * ChangeProgram page — lets an authenticated user switch their program
 * using the same persona/program picker from onboarding.
 *
 * Key behaviour:
 *   - Does NOT delete past scheduled activities
 *   - Appends the new program's activities starting from today
 *   - Updates the user's currentProgramId, personaId, selectedProgramId
 */
function ChangeProgram() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBuilding, setShowBuilding] = useState(false);
  const [buildingProgramName, setBuildingProgramName] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || currentUser.uid !== userId) {
        navigate('/signin');
        return;
      }
      const profile = await getUserProfile(userId);
      setUser(profile);
    });
    return () => unsubscribe();
  }, [userId, navigate]);

  const handleSelect = async (personaId, programId, programData) => {
    setLoading(true);

    try {
      await updateUserProfile(userId, {
        personaId,
        selectedProgramId: programId,
        programId: programData?.programId || null,
      });

      if (programData?.programId && programData?.status === 'available') {
        setBuildingProgramName(programData.name || 'New');
        setShowBuilding(true);
        try {
          const result = await setupProgramByPersona(userId, programData.programId);
          console.log('[ChangeProgram] Program setup result:', result);
        } catch (err) {
          console.error('[ChangeProgram] Program setup error:', err);
        }
      } else {
        navigate(`/${userId}/dashboard`);
      }
    } catch (err) {
      console.error('[ChangeProgram] Error:', err);
      setLoading(false);
    }
  };

  if (showBuilding) {
    return (
      <BuildingPlanScreen
        personaName={buildingProgramName}
        onComplete={() => navigate(`/${userId}/dashboard`, { state: { refreshed: Date.now() } })}
      />
    );
  }

  if (!user) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-main">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page has-bottom-nav">
      <div className="dashboard-bg-glow dashboard-bg-glow-1" />
      <div className="dashboard-bg-glow dashboard-bg-glow-2" />
      <div className="dashboard-overlay" />
      <DashboardSidebar userId={userId} user={user} />
      <div className="dashboard-main">
        <div style={{ padding: '16px 0 0' }}>
          <button
            type="button"
            className="onboarding-back"
            onClick={() => navigate(`/${userId}/settings`)}
          >
            ← Back to Settings
          </button>
        </div>
        <ProgramSelector
          initialPersonaId={user?.personaId || ''}
          initialProgramId=""
          onSelect={handleSelect}
          submitLabel="Switch Program"
          loading={loading}
        />
      </div>
      <BottomNav userId={userId} user={user} />
    </div>
  );
}

export default ChangeProgram;
